import { usePostFrontFile } from '@framework/api-client';
import {
  AlignmentSelectInput,
  BaseLayoutColSpanInput,
  ButtonAlign,
  ButtonType,
  ButtonTypeSelectInput,
  CommonBaseLayoutSchema,
  CommonGraphicWidgetConfigSchema,
  IWidgetComponentProps,
  IconType,
  IconTypeSelectInput,
  RichTextAdvancedInput,
  RichTextAdvancedSchema,
  getWidgetProps,
} from '@framework/engine';
import { FormItem, MenuPresets, UploadInput, useField } from '@framework/form';
import { RowElement } from '@framework/ui';
import { yup } from '@framework/validation';
import { UploadFile, message } from 'antd';
import React, { useCallback, useEffect, useState } from 'react';
import { IDownloadWidget, WidgetDownloadType } from './IDownloadWidget';

export const DownloadWidgetConfigSchema: yup.ObjectSchema<IDownloadWidget> =
  CommonGraphicWidgetConfigSchema.shape({
    type: yup.string().exact(WidgetDownloadType).required(),
    label: RichTextAdvancedSchema.optional().label('Etichetta'),

    layout: CommonBaseLayoutSchema,
    filePath: yup.string().label('File').required(),
  });

export interface IDownloadWidgetConfigComponentProps
  extends IWidgetComponentProps<IDownloadWidget> {}

/**
 * Configurazione del widget Download
 */
export function DownloadWidgetConfigComponent(
  props: IDownloadWidgetConfigComponentProps,
) {
  const options = getWidgetProps(props);
  const uploadMutation = usePostFrontFile();
  const [fileList, setFileList] = useState<UploadFile[]>();
  const filePathField = useField('filePath');

  const handleCustomRequest = useCallback(
    (options: any) => {
      const { file, onError } = options;
      if (!file) {
        console.error('[Upload] Error: file not found');
        return;
      }

      uploadMutation
        .mutateAsync({
          data: { file: file },
        })
        .then(filePath => {
          filePathField.setValue(filePath);
          setFileList([file]);
          message.success('File caricato con successo');
        })
        .catch(err => {
          console.error('[Upload] Error after API', err);
          onError?.(err);
          message.error( err.response?.data?.descrizione ?? 'Errore durante il caricamento del file'); // prettier-ignore
        });
    },
    [filePathField, uploadMutation],
  );

  const handleRemove = useCallback(() => {
    setFileList([]);
    filePathField.setValue('');
  }, [filePathField]);

  // Carica il file selezionato
  useEffect(() => {
    if (props.widget.filePath) {
      const [_, fileName] = props.widget.filePath.split('/');
      const file: UploadFile = {
        uid: '-1',
        name: fileName,
        status: 'done',
      };

      setFileList([file]);
    }
  }, [props.widget.filePath]);

  return (
    <div>
      <BaseLayoutColSpanInput isTableColumn={options.isTableColumn} />
      <RowElement>
        <FormItem name="layout.buttonType" label="Tipo di pulsante">
          <ButtonTypeSelectInput
            name="layout.buttonType"
            defaultValue={ButtonType.Primary}
            placeholder="Seleziona il tipo di pulsante"
          />
        </FormItem>
        <FormItem name="layout.iconType" label="Icona del pulsante">
          <IconTypeSelectInput
            name="layout.iconType"
            defaultValue={IconType.Nessuna}
            placeholder="Seleziona l'icona del pulsante"
          />
        </FormItem>
        <FormItem name="layout.buttonAlign" label="Allineamento pulsante">
          <AlignmentSelectInput
            name="layout.buttonAlign"
            defaultValue={ButtonAlign.End}
            placeholder="Seleziona l'allineamento"
          />
        </FormItem>
      </RowElement>
      <FormItem
        name="filePath"
        help="Carica il file da poter scaricare in fase di compilazione"
      >
        <UploadInput
          name="filePath"
          buttonProps={{ size: 'large' }}
          customRequest={handleCustomRequest}
          fileList={fileList}
          onRemove={handleRemove}
        />
      </FormItem>
      <FormItem name="label" label="Testo pulsante download">
        <RichTextAdvancedInput
          name="label"
          rows={2}
          menus={MenuPresets.Minimal}
        />
      </FormItem>
    </div>
  );
}
